import { NO_ROTATION } from './../constants';
import { GenderEnum } from '@/enum/gender';
import { MixAndMatchCategoryEnum } from '@/enum/mix_and_match';
import { StyleEnum } from '@/enum/style';
import ClientBuild from '@/services/ClientBuild';
import FeatureGating from './FeatureGating';

import base64 from 'base64-js';

const RENDERING_PRODUCTION_CDN: string = 'https://images.bitmoji.com';
const RENDERING_STAGING_CDN: string = 'https://dcgro32pny54o.cloudfront.net';

const getServiceBaseUrl = (): string => {
  if (ClientBuild.getClientApp() === ClientBuild.ClientAppEnum.Snapchat) {
    if (BUILD_FLAGS.ENVIRONMENT === 'production') {
      // The images CDN is zero-rated and must only be used inside the Snapchat apps
      return RENDERING_PRODUCTION_CDN;
    } else {
      return RENDERING_STAGING_CDN;
    }
  } else {
    if (BUILD_FLAGS.ENVIRONMENT === 'production') {
      return 'https://preview.bitmoji.com';
    } else {
      return RENDERING_STAGING_CDN;
    }
  }
};

const BASE_PREVIEW_PATH = '/bm-preview/v3/avatar';

const getPreviewBaseUrl = (): string => {
  return getServiceBaseUrl() + BASE_PREVIEW_PATH;
};

const getAssetUrl = (genderInt: GenderEnum, styleInt: StyleEnum): string => {
  const path = FeatureGating.useAssetsService()
    ? '/avatar-assets-service/v3/assets?useProto=1'
    : '/avatar-builder-v3/assets_v3?useProto=1';
  let assetUrl = getServiceBaseUrl() + path;

  if (genderInt && styleInt) {
    assetUrl += `&gender=${genderInt}&style=${styleInt}`;
  }
  return assetUrl;
};

const MAIN_PREVIEW_SCALE: number = 1;
const MAIN_LARGE_PREVIEW_SCALE = 2;
const LARGE_PREVIEW_WINDOW_MIN_SIZE = 750;
const OPTION_PREVIEW_SCALE: number = 0.75;
const MIX_AND_MATCH_OPTION_PREVIEW_SCALE: number = 0.5;
const MIX_AND_MATCH_BOTTOM_OPTION_PREVIEW_SCALE: number = 1;

const OUTFIT_PREVIEW_TYPES = [
  'mannequin-with-head-left',
  'mannequin-with-head-center',
  'mannequin-with-head-right',
];
const OUTFIT_PREVIEW_ROTATIONS = [7, 0, 1];

// Generate preview URL for main avatar image
const getMainPreview = (
  rotation: number,
  traits: Record<string, number>,
  category: string,
): string => {
  // Use higher definition image for large screens
  const previewType = category === 'tonguering' ? 'body-tongue' : 'body';
  const scale =
    window.innerWidth > LARGE_PREVIEW_WINDOW_MIN_SIZE &&
    window.innerHeight > LARGE_PREVIEW_WINDOW_MIN_SIZE
      ? MAIN_LARGE_PREVIEW_SCALE
      : MAIN_PREVIEW_SCALE;
  return generatePreviewUrl(previewType, scale, rotation, traits);
};

// Generate preview URL for category option images
const getOptionPreview = (
  previewType: string,
  rotation: number | null,
  traits: Record<string, number>,
  cacheable: boolean = false,
): string => {
  let url = generatePreviewUrl(previewType, OPTION_PREVIEW_SCALE, rotation, traits);
  if (cacheable) {
    url += '&cacheable=1';
  }
  return url;
};

// Generate preview URL for legacy outfits (intended for special cases where the server
// response must be updated due to sixth body filtering)
const getLegacyOutfitPreview = (
  outfitId: number,
  outfitIndex: number,
  gender: GenderEnum,
): string => {
  const outfitCol = outfitIndex % 3;
  const previewType = OUTFIT_PREVIEW_TYPES[outfitCol];
  const rotation = OUTFIT_PREVIEW_ROTATIONS[outfitCol];
  return `${getPreviewBaseUrl()}/${previewType}?scale=1&gender=${gender}&style=5&rotation=${rotation}&outfit=${outfitId}&ua=2`;
};

// Construct preview URL with consistent trait ordering.
// Uses webp for platforms that support it.
const generatePreviewUrl = (
  previewType: string,
  scale: number,
  rotation: number | null,
  traits: Record<string, number>,
): string => {
  const gender = traits.gender;
  const style = traits.style;

  const serializedTraits = serializeTraits(traits);
  const previewBaseUrl = getPreviewBaseUrl();

  const previewRotation = rotation === NO_ROTATION ? '' : `&rotation=${rotation}`;

  return `${previewBaseUrl}/${previewType}?scale=${scale}&gender=${gender}&style=${style}${previewRotation}${serializedTraits}&ua=2`;
};

const getMixAndMatchOptionPreview = (
  category: string,
  values: Record<string, number>,
  gender: GenderEnum,
  isBodysuit: boolean,
): string => {
  let valuesToSerialize = values;

  if (isBodysuit) {
    Object.keys(values).forEach((categoryKey) => {
      const bottomKey = categoryKey.replace(
        MixAndMatchCategoryEnum.TOP,
        MixAndMatchCategoryEnum.BOTTOM,
      );
      valuesToSerialize[bottomKey] = values[categoryKey];
    });
  }

  const serializedTraits = serializeTraits(valuesToSerialize);
  const previewBaseUrl = getPreviewBaseUrl();

  const previewScale = `scale=${
    category === MixAndMatchCategoryEnum.BOTTOM
      ? MIX_AND_MATCH_BOTTOM_OPTION_PREVIEW_SCALE
      : MIX_AND_MATCH_OPTION_PREVIEW_SCALE
  }`;

  return `${previewBaseUrl}/${category}?${previewScale}&gender=${gender}&style=5${serializedTraits}&clothing_type=1&ua=2`;
};

/**
 * Serialize traits dictionary to be used in preview URL.
 * Sort the trait keys to ensure the order of the traits is consistent.
 *
 * Order is as follows:
 *   - 1. Scale
 *   - 2. Gender
 *   - 3. Style
 *   - 4. Rotation
 *   - 5. Traits in alphabetical order
 *
 * @param  {Dictionary} traits  Dictionary of: {trait_key: trait_value}
 * @return {String}             String of traits to be appended to preview URL
 */
const serializeTraits = (selectedTraits: Record<string, number>) => {
  const traits = Object.assign({}, selectedTraits);

  // Delete keys we wouldn't want to be included in serialization
  delete traits.scale;
  delete traits.gender;
  delete traits.style;
  delete traits.rotation;

  // Sort trait keys alphabetically
  const sortedTraitKeys = Object.keys(traits).sort((a, b) => {
    const nameA = a.toUpperCase(); // ignore upper and lowercase
    const nameB = b.toUpperCase(); // ignore upper and lowercase
    if (nameA < nameB) {
      return -1;
    }
    if (nameA > nameB) {
      return 1;
    }
    return 0;
  });

  return sortedTraitKeys.map((key) => `&${key}=${traits[key]}`).join('');
};

const transformOutfitImageBaseURL = (urlString: string): string => {
  // TODO (kc): this is temporary at moving the preview url
  // will have to remove this once all the preview url has been migrated
  // this if statement check if the preview url if the new format of just the parameters
  // e.g /avatar-builder-v3/preview/mannequin-left? ..... rather than the previous
  // https://bitmoji.api.snapchat.com/api/avatar-builder-v3/preview/mannequin-left? ...

  if (urlString.charAt(0) === '/') {
    return `${getServiceBaseUrl()}${urlString}`;
  }

  // this only happens if we still get our preview image preview in the form of
  // https://bitmoji.api.snapchat.com/api/avatar-builder-v3/preview/ ...
  const url = new URL(urlString);
  let newUrlString;
  if (url.searchParams) {
    url.searchParams.set('scale', `${OPTION_PREVIEW_SCALE}`);
    newUrlString = url.toString();
  } else {
    // Android 4.4.4 hack as it doesn't support URLSearchParams
    newUrlString = url.toString().replace('scale=1', `scale=${OPTION_PREVIEW_SCALE}`);
  }
  // Also ignore "/api/" prefix baked into outfit images returned from server
  return newUrlString
    .replace(url.protocol + '//' + url.host, getServiceBaseUrl())
    .replace('/api', '');
};

async function generate3dAvatarParamsFromCharData(
  jsonCharData: Record<string, unknown>,
): Promise<Record<string, string | number>> {
  const params: Record<string, string | number> = {};
  return import('pako').then((module) => {
    const deflate = module.deflate;
    if (jsonCharData.pd2) {
      params.pd2 = JSON.stringify(jsonCharData.pd2);
    }
    if (jsonCharData.facial_features) {
      params.facial_features = JSON.stringify(jsonCharData.facial_features);
    }
    if (jsonCharData.colours) {
      params.colours = JSON.stringify(jsonCharData.colours);
    }
    if (jsonCharData.body) {
      const rawBody = JSON.stringify(jsonCharData.body);
      const bytes: Uint8Array = deflate(rawBody);
      params.body = encodeURIComponent(base64.fromByteArray(bytes));
      params.body_encoded = 'true';
    }
    if (jsonCharData.sex && typeof jsonCharData.sex === 'number') {
      params.sex = jsonCharData.sex;
    }
    if (jsonCharData.proportion && typeof jsonCharData.proportion === 'number') {
      params.proportion = jsonCharData.proportion;
    }
    return params;
  });
}

export default {
  BASE_PREVIEW_PATH,
  OUTFIT_PREVIEW_TYPES,
  OUTFIT_PREVIEW_ROTATIONS,
  getServiceBaseUrl,
  getMainPreview,
  getOptionPreview,
  getLegacyOutfitPreview,
  getMixAndMatchOptionPreview,
  transformOutfitImageBaseURL,
  generate3dAvatarParamsFromCharData,
  getAssetUrl,
};
